import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFile } from '../../services/fileUploadService';
import {
  ContactInfoDTO,
  CreditReferenceDTO,
  DonationPurposeDTO,
  buildCreateCreditApplicationRequest,
  createCreditApplication,
  fetchDonationPurposes,
} from '../../services/creditService';
import '../../styles/AccountTools.css';

const emptyReference: CreditReferenceDTO = { name: '', relationship: '', phone: '', email: '' };

type CreditApplyMessage = { type: 'success' | 'error'; title?: string; text: string } | null;

const filePreviewUrl = (file: File) => (file.type.startsWith('image/') ? URL.createObjectURL(file) : '');

const CreditApply = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id || '';

  const [description, setDescription] = useState('');
  const [purposes, setPurposes] = useState<DonationPurposeDTO[]>([]);
  const [selectedPurposeIds, setSelectedPurposeIds] = useState<string[]>([]);
  const [passportFile, setPassportFile] = useState<File | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [contactInfo, setContactInfo] = useState<ContactInfoDTO>({ name: '', phone: '', email: '' });
  const [references, setReferences] = useState<CreditReferenceDTO[]>([]);
  const [draftReference, setDraftReference] = useState<CreditReferenceDTO>(emptyReference);
  const [message, setMessage] = useState<CreditApplyMessage>(null);
  const [loadingPurposes, setLoadingPurposes] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadPurposes = useCallback(async () => {
    setLoadingPurposes(true);
    try {
      const page = await fetchDonationPurposes({ page: 0 });
      setPurposes(page.content || []);
    } catch (error) {
      setMessage({ type: 'error', title: 'Error', text: error instanceof Error ? error.message : 'Failed to load categories' });
    } finally {
      setLoadingPurposes(false);
    }
  }, []);

  useEffect(() => {
    loadPurposes();
  }, [loadPurposes]);

  if (!userId) {
    return (
      <main className="tool-page">
        <div className="tool-card tool-empty">Sign in to apply for credit.</div>
      </main>
    );
  }

  const togglePurpose = (id: string) => {
    setSelectedPurposeIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const addReference = () => {
    if (!draftReference.name.trim() || !draftReference.relationship.trim()) {
      setMessage({ type: 'error', title: 'Validation', text: 'Reference name and relationship are required.' });
      return;
    }
    setReferences((prev) => [...prev, {
      name: draftReference.name.trim(),
      relationship: draftReference.relationship.trim(),
      phone: draftReference.phone?.trim() || undefined,
      email: draftReference.email?.trim() || undefined,
    }]);
    setDraftReference(emptyReference);
  };

  const uploadFiles = async (files: File[], fileType: 'PHOTO' | 'DOCUMENT') => {
    const ids: string[] = [];
    for (const file of files) {
      const result = await uploadFile(userId, file, { purpose: 'USER_PROFILE', fileType });
      ids.push(result.fileId);
    }
    return Array.from(new Set(ids));
  };

  const renderFileReview = (
    title: string,
    files: File[],
    onReplace: (index: number, file: File) => void,
    onRemove: (index: number) => void
  ) => {
    if (!files.length) return null;
    return (
      <div className="tool-upload-review">
        {files.map((file, index) => {
          const previewUrl = filePreviewUrl(file);
          return (
            <div className="tool-upload-card" key={`${title}-${file.name}-${index}`}>
              <div className="tool-upload-preview">
                {previewUrl ? <img src={previewUrl} alt={`${title} ${index + 1}`} /> : <span>{file.type || 'File'}</span>}
              </div>
              <div className="tool-upload-meta">
                <strong>{files.length === 1 ? title : `${title} ${index + 1}`}</strong>
                <span>{file.name}</span>
                <small>{Math.ceil(file.size / 1024)} KB selected</small>
              </div>
              <div className="tool-upload-actions">
                <label>
                  Replace
                  <input
                    type="file"
                    accept={file.type.startsWith('image/') ? 'image/*' : undefined}
                    onChange={(event) => event.target.files?.[0] && onReplace(index, event.target.files[0])}
                  />
                </label>
                <button type="button" onClick={() => onRemove(index)}>Remove</button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!userId) {
      setMessage({ type: 'error', title: 'Error', text: 'User not found.' });
      return;
    }
    if (selectedPurposeIds.length === 0) {
      setMessage({ type: 'error', title: 'Validation', text: 'Select at least one category.' });
      return;
    }
    if (!description.trim()) {
      setMessage({ type: 'error', title: 'Validation', text: 'Please provide a short description.' });
      return;
    }
    if (!passportFile) {
      setMessage({ type: 'error', title: 'Validation', text: 'Passport photo is required.' });
      return;
    }

    setSubmitting(true);
    try {
      const passportPhoto = (await uploadFiles([passportFile], 'PHOTO'))[0];
      const photoIds = await uploadFiles(photos, 'PHOTO');
      const documentIds = await uploadFiles(documents, 'DOCUMENT');
      const contact =
        contactInfo.name.trim()
          ? {
              name: contactInfo.name.trim(),
              phone: contactInfo.phone?.trim() || undefined,
              email: contactInfo.email?.trim() || undefined,
            }
          : null;

      await createCreditApplication(buildCreateCreditApplicationRequest({
        description,
        purposeIds: selectedPurposeIds,
        contactInfo: contact,
        references,
        passportPhotoId: passportPhoto,
        photoIds,
        documentIds,
      }));

      setMessage({ type: 'success', title: 'Submitted', text: 'Your application has been submitted.' });
      window.setTimeout(() => navigate('/account/credits/applications'), 1000);
    } catch (error) {
      setMessage({ type: 'error', title: 'Error', text: error instanceof Error ? error.message : 'Failed to submit application' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <h1>Credit Application Form</h1>
          <p>Credits are used only within Olitask to request help.</p>
        </div>
      </div>

      {message && (
        <div className={`tool-message ${message.type}`} role="status" aria-live="polite">
          {message.title && <strong>{message.title}</strong>}
          <span>{message.text}</span>
        </div>
      )}

      <form className="tool-grid" onSubmit={handleSubmit}>
        <section className="tool-card tool-form">
          <h2>Application Details</h2>
          <div className="tool-field">
            <label>Description</label>
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Explain why you need this credit." />
          </div>
          <div className="tool-field">
            <label>Passport Photo</label>
            <input type="file" accept="image/*" onChange={(event) => setPassportFile(event.target.files?.[0] || null)} />
            {passportFile && renderFileReview('Passport Photo', [passportFile], (_, file) => setPassportFile(file), () => setPassportFile(null))}
          </div>
          <div className="tool-field">
            <label>Photos</label>
            <input type="file" accept="image/*" multiple onChange={(event) => setPhotos(Array.from(event.target.files || []))} />
            {renderFileReview(
              'Photo',
              photos,
              (index, file) => setPhotos((prev) => prev.map((item, itemIndex) => (itemIndex === index ? file : item))),
              (index) => setPhotos((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
            )}
          </div>
          <div className="tool-field">
            <label>Documents</label>
            <input type="file" multiple onChange={(event) => setDocuments(Array.from(event.target.files || []))} />
            {renderFileReview(
              'Document',
              documents,
              (index, file) => setDocuments((prev) => prev.map((item, itemIndex) => (itemIndex === index ? file : item))),
              (index) => setDocuments((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
            )}
          </div>
        </section>

        <section className="tool-card tool-form">
          <h2>Categories</h2>
          {loadingPurposes ? (
            <div className="tool-loading">Loading categories...</div>
          ) : (
            <div className="tool-chips">
              {purposes.map((purpose) => (
                <button
                  className={`tool-chip ${selectedPurposeIds.includes(purpose.id) ? 'active' : ''}`}
                  key={purpose.id}
                  type="button"
                  onClick={() => togglePurpose(purpose.id)}
                >
                  {purpose.name}
                </button>
              ))}
            </div>
          )}

          <h2>Contact Information</h2>
          <div className="tool-field">
            <label>Name</label>
            <input value={contactInfo.name} onChange={(event) => setContactInfo((prev) => ({ ...prev, name: event.target.value }))} />
          </div>
          <div className="tool-field">
            <label>Phone</label>
            <input value={contactInfo.phone || ''} onChange={(event) => setContactInfo((prev) => ({ ...prev, phone: event.target.value }))} />
          </div>
          <div className="tool-field">
            <label>Email</label>
            <input type="email" value={contactInfo.email || ''} onChange={(event) => setContactInfo((prev) => ({ ...prev, email: event.target.value }))} />
          </div>
        </section>

        <section className="tool-card tool-form">
          <h2>References</h2>
          <div className="tool-field">
            <label>Name</label>
            <input value={draftReference.name} onChange={(event) => setDraftReference((prev) => ({ ...prev, name: event.target.value }))} />
          </div>
          <div className="tool-field">
            <label>Relationship</label>
            <input value={draftReference.relationship} onChange={(event) => setDraftReference((prev) => ({ ...prev, relationship: event.target.value }))} />
          </div>
          <div className="tool-field">
            <label>Phone</label>
            <input value={draftReference.phone || ''} onChange={(event) => setDraftReference((prev) => ({ ...prev, phone: event.target.value }))} />
          </div>
          <div className="tool-field">
            <label>Email</label>
            <input type="email" value={draftReference.email || ''} onChange={(event) => setDraftReference((prev) => ({ ...prev, email: event.target.value }))} />
          </div>
          <button className="tool-btn-secondary" type="button" onClick={addReference}>Add Reference</button>
          {references.map((reference, index) => (
            <div className="tool-row" key={`${reference.name}-${index}`}>
              <div className="tool-row-main">
                <div className="tool-row-title">{reference.name}</div>
                <div className="tool-row-sub">{reference.relationship}</div>
              </div>
              <button className="tool-btn-danger" type="button" onClick={() => setReferences((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}>
                Remove
              </button>
            </div>
          ))}
        </section>

        <section className="tool-card">
          <h2>Submit</h2>
          <p>The payload sent matches the mobile app create credit application request.</p>
          <div className="tool-inline">
            <button className="tool-btn-secondary" type="button" onClick={() => navigate('/account/credits')}>Cancel</button>
            <button className="tool-btn" type="submit" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit'}</button>
          </div>
        </section>
      </form>
    </main>
  );
};

export default CreditApply;
